import { Component, input, output, signal, ViewChild } from '@angular/core';
import {
	_,
	ALL_EXTERNAL_ASSIGNABLE_ROLES,
	ALL_ROLES,
	ClientPurchase,
	ClientPurchaseItem,
	getCsvSpec,
	getCsvSpecification,
	getCsvTemplate,
	Role,
	Rubric,
	RubricLevel,
	SetupWizardPayload,
	SetupWizardPayloadObjectKey,
	SetupWizardPayloadObjectKeys,
	SetupWizardState,
	SetupWizardSteps,
	Skill,
} from '@mira-avant/mtlibs';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { FileHelper } from '../../../../../utils/file.helper';
import { PurchaseService } from '../../../../../services/purchase';
import { RubricService } from '../../../../../services/rubric.service';
import { SkillService } from '../../../../../services/skill.service';
import { AuthService } from '../../../../../services/auth.service';
import { Dropzone, DropzoneFile } from '../../../dropzone/dropzone';
import { NavigationService } from '../../../../../services/navigation.service';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { SetupWizardCsvFileManager } from './setup-wizard-csv-file-manager/setup-wizard-csv-file-manager';
import { SetupWizardPreviewStats } from '../setup-wizard-preview-stats/setup-wizard-preview-stats';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

@Component({
	selector: 'app-setup-wizard-upload-csvs',
	imports: [NzIconModule, Dropzone, NzButtonModule, SetupWizardCsvFileManager, SetupWizardPreviewStats],
	standalone: true,
	templateUrl: './setup-wizard-upload-csvs.html',
	styleUrl: './setup-wizard-upload-csvs.scss',
})
export class SetupWizardUploadCsvs {
	@ViewChild('fileManager') public fileManager!: SetupWizardCsvFileManager;
	public onApplyToPreview = output<SetupWizardPayload>();
	public selectedSteps = input<SetupWizardSteps[]>();
	public SetupWizardSteps = SetupWizardSteps;
	public isAdmin = signal(false);
	public SetupWizardState = SetupWizardState;
	public SetupWizardPayloadObjectKeys = SetupWizardPayloadObjectKeys;
	public preview = input<SetupWizardPayload>();
	private clientUuid: string | undefined;
	private sub?: Subscription;

	constructor(
		private route: ActivatedRoute,
		private purchaseService: PurchaseService,
		private rubricService: RubricService,
		private skillService: SkillService,
		private authService: AuthService,
		public nav: NavigationService
	) {
		this.authService.isAdmin$().then((isAdmin: boolean) => {
			this.isAdmin.set(isAdmin);
		});

		this.sub = this.route.params.subscribe(params => {
			this.clientUuid = params['clientUuid'];
		});
	}

	ngOnInit() {}

	public async onDropCompleted($event: DropzoneFile[]) {
		$event.forEach(async file => this.fileManager.receiveNewFile(file));
	}

	public onApprovedFile(payload: SetupWizardPayload) {
		this.onApplyToPreview.emit(payload);
	}

	/**
	 * This file will show the user what overall CSV rules we have such as using "" for a quote.
	 *
	 * These are the rules:
	 *
	 * 1. Values are separated by commas without spaces such as: one,two not one, two.
	 * 2. Text fields are not surrounded by double quotes unless the column contains a comma.
	 * 3. Escaping quotes: use "" for escaping quote.
	 * 4. Line endings: newline character \n.
	 * 5. Header row - first row must be header row.
	 * 6. empty fields are represented by consecutive commase such as ,,
	 * 7. No training comma.
	 *
	 * @public
	 */
	public downloadCsvSpec() {
		FileHelper.saveString(getCsvSpec(), 'csv-specifications.txt');
	}

	public downloadSkills() {
		this.skillService.getSkills$().then((skills: Skill[]) => {
			let output: string = 'The following skill codes are available for use in the CSV files:\n\n';

			skills.forEach(skill => {
				output += `${skill.code}\n`;
			});

			FileHelper.saveString(output, 'skills.txt');
		});
	}

	public downloadGroups() {
		const roles: Role[] = this.isAdmin() ? ALL_ROLES : ALL_EXTERNAL_ASSIGNABLE_ROLES;

		let output: string = 'The following group names are available for use in the CSV file:\n\n';

		roles.forEach((role: Role) => {
			output += `${role}\n`;
		});

		FileHelper.saveString(output, 'groups.txt');
	}

	public async downloadRubrics() {
		this.rubricService.getRubrics$().then(async (rubrics: Rubric[]) => {
			let output: string =
				'The rubric level number values will be listed under each rubric code.' +
				' You can use the exact spelling of the rubric codes and the exact number values present in this document in the other CSV templates.\n\n';

			const rubricNames = await Promise.all(
				rubrics.map(async (rubric: Rubric) => {
					output += `${rubric.code}\n`;

					const rubricLevels: RubricLevel[] = await this.rubricService.getRubricLevels$(rubric.code);

					rubricLevels.forEach((level: RubricLevel) => {
						output += `\t${level.level}\n`;
					});
				})
			);

			FileHelper.saveString(output, 'rubrics.txt');
		});
	}

	public async downloadYourProducts() {
		if (!this.clientUuid) {
			throw new Error('Client UUID is required for Setup Wizard Upload CSVs component');
		}

		this.purchaseService.getPurchasesByClient$(this.clientUuid).then(async (purchases: ClientPurchase[]) => {
			const allClientPurchaseItems: ClientPurchaseItem[] = purchases.flatMap(
				(purchase: ClientPurchase) => purchase.client_purchase_item || []
			);

			const uniqueProductNames: string[] = Array.from(
				new Set(allClientPurchaseItems.map((item: ClientPurchaseItem) => item.product_name))
			);

			const productListString = uniqueProductNames.join('\n');

			FileHelper.saveString(productListString, 'your-products.txt');
		});
	}

	public downloadStepCsvTemplate(specificType: SetupWizardPayloadObjectKey) {
		FileHelper.saveString(getCsvTemplate(specificType), `setup-wizard-${specificType}-template.csv`);
	}

	public downloadStepCsvSpecification(specificType: SetupWizardPayloadObjectKey) {
		FileHelper.saveString(getCsvSpecification(specificType), `setup-wizard-${specificType}-spec.txt`);
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
